import { Button, Dialog, Icon, IconSizeClasses, Input, Label } from '@eppendorf/vnls-react-components';
import type { Organization } from '@eppendorf/vnls-user-tenant-utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { isOrganizationDeletionExcluded } from '$features/organizations/delete-organization-exclusions';
import { useDeleteOrganization } from '$features/organizations/organizations.api';

interface Props {
  organization: Pick<Organization, 'id' | 'name'>;
  onClose: () => void;
  onDeleted: () => void;
}

export function DeleteOrganizationDialog({ organization, onClose, onDeleted }: Props) {
  const { t } = useTranslation();
  const [enteredName, setEnteredName] = useState('');
  const { mutateAsync, isPending } = useDeleteOrganization();
  const isExcluded = isOrganizationDeletionExcluded(organization.id);

  const handleDelete = async () => {
    if (isExcluded || !enteredName || enteredName !== organization.name || isPending) return;

    try {
      await mutateAsync(organization.id);
      toast(t('deleteOrganization.success'), { type: 'success' });
      onDeleted();
    } catch {
      toast(t('deleteOrganization.error'), { type: 'error' });
    }
  };

  return (
    <Dialog isOpen hideCloseButton onOpenChange={(open) => !open && !isPending && onClose()}>
      <div className="flex flex__dir--column flex__ai--center text-align-center">
        <Icon name="warning" className="bg-orange-500 m-bottom-xxl" size={IconSizeClasses.XLarge} />
        <Dialog.Title>{t(isExcluded ? 'deleteOrganization.notAllowedTitle' : 'deleteOrganization.title')}</Dialog.Title>
        {isExcluded ? (
          <Dialog.Description>{t('deleteOrganization.notAllowedMessage', { name: organization.name })}</Dialog.Description>
        ) : (
          <>
            <Dialog.Description className="m-bottom-l">{t('deleteOrganization.warning')}</Dialog.Description>
            <p className="m-bottom-m">{t('deleteOrganization.confirmName', { name: organization.name })}</p>
            <Label htmlFor="delete-organization-name">{t('deleteOrganization.nameLabel')}</Label>
            <Input
              id="delete-organization-name"
              type="text"
              className="input m-top-xs w-full"
              value={enteredName}
              onChange={(event) => setEnteredName(event.target.value)}
              disabled={isPending}
            />
          </>
        )}
      </div>
      <Dialog.Actions>
        <Button variant="secondary" onClick={onClose} disabled={isPending}>
          {t(isExcluded ? 'shared.close' : 'shared.cancel')}
        </Button>
        {!isExcluded && (
          <Button variant="primary" onClick={handleDelete} disabled={!enteredName || enteredName !== organization.name || isPending}>
            {t('shared.delete')}
          </Button>
        )}
      </Dialog.Actions>
    </Dialog>
  );
}
